import {
  CompanyActionPermission,
  CompanyPermission,
  REPAIR_MAINTENANCE_MENU,
} from '../models/company-permission.model';
import {
  RM_PERMISSION_MODULE,
  RmAction,
  RmActionName,
  RmBackendAction,
} from '../models/rm-actions.model';
import {
  getCompanyPermissionForCompany,
  hasPageUrlInMenu,
  usesApiPagePermissions,
} from './company-permission.util';

type RmActionMap = Map<string, boolean>;

function buildRmActionMap(
  actionPermissions: CompanyActionPermission[]
): RmActionMap {
  const map: RmActionMap = new Map();

  for (const permission of actionPermissions) {
    if (permission.module === RM_PERMISSION_MODULE) {
      map.set(permission.action, permission.isAllowed === true);
    }
  }

  return map;
}

function hasRmAction(map: RmActionMap, action: string): boolean {
  return map.has(action);
}

function isRmActionAllowed(map: RmActionMap, action: string): boolean {
  return map.get(action) === true;
}

function anyRmActionAllowed(map: RmActionMap, actions: string[]): boolean {
  return actions.some((action) => isRmActionAllowed(map, action));
}

function anyRmActionPresent(map: RmActionMap, actions: string[]): boolean {
  return actions.some((action) => hasRmAction(map, action));
}

export function hasRmModuleActionPermissions(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  return Boolean(
    companyPermission?.actionPermissions?.some(
      (permission) => permission.module === RM_PERMISSION_MODULE
    )
  );
}

export function usesRmBePermissions(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  return (
    usesApiPagePermissions(companyPermission) ||
    hasRmModuleActionPermissions(storedCompanyPermissions, companyId)
  );
}

export function resolveRmFeActionFromBeActionMap(
  map: RmActionMap,
  feAction: RmActionName | string,
  companyPermission?: CompanyPermission | null
): boolean | undefined {
  switch (feAction) {
    case RmAction.View: {
      if (isRmActionAllowed(map, RmBackendAction.View)) {
        return true;
      }

      if (hasRmAction(map, RmBackendAction.View)) {
        return false;
      }

      if (
        hasPageUrlInMenu(
          companyPermission,
          REPAIR_MAINTENANCE_MENU,
          'repairs-and-maintenance/list'
        )
      ) {
        return true;
      }

      return undefined;
    }

    case RmAction.Create: {
      if (
        anyRmActionAllowed(map, [
          RmBackendAction.Create,
          RmBackendAction.CreateDraft,
        ])
      ) {
        return true;
      }

      if (
        anyRmActionPresent(map, [
          RmBackendAction.Create,
          RmBackendAction.CreateDraft,
        ])
      ) {
        return false;
      }

      return undefined;
    }

    case RmAction.DeleteDraft: {
      if (isRmActionAllowed(map, RmBackendAction.DeleteDraft)) {
        return true;
      }

      if (hasRmAction(map, RmBackendAction.DeleteDraft)) {
        return false;
      }

      if (isRmActionAllowed(map, RmBackendAction.UpdateDraft)) {
        return true;
      }

      if (hasRmAction(map, RmBackendAction.UpdateDraft)) {
        return false;
      }

      return undefined;
    }

    // Some roles (e.g. RM Manager) only get AddQuotation from BE, no SubmitQuotation.
    case RmAction.SubmitQuotation: {
      if (isRmActionAllowed(map, RmBackendAction.SubmitQuotation)) {
        return true;
      }

      if (hasRmAction(map, RmBackendAction.SubmitQuotation)) {
        return false;
      }

      if (isRmActionAllowed(map, RmBackendAction.AddQuotation)) {
        return true;
      }

      if (hasRmAction(map, RmBackendAction.AddQuotation)) {
        return false;
      }

      return undefined;
    }

    case RmAction.Withdraw: {
      if (isRmActionAllowed(map, RmBackendAction.Withdraw)) {
        return true;
      }

      if (hasRmAction(map, RmBackendAction.Withdraw)) {
        return false;
      }

      if (isRmActionAllowed(map, RmBackendAction.DeleteDraft)) {
        return true;
      }

      if (hasRmAction(map, RmBackendAction.DeleteDraft)) {
        return false;
      }

      return undefined;
    }

    case RmAction.AssignVendor:
    case RmAction.InviteVendor: {
      if (
        anyRmActionAllowed(map, [
          RmBackendAction.InviteVendor,
          RmBackendAction.AssignVendor,
        ])
      ) {
        return true;
      }

      if (
        anyRmActionPresent(map, [
          RmBackendAction.InviteVendor,
          RmBackendAction.AssignVendor,
        ])
      ) {
        return false;
      }

      return undefined;
    }

    case RmAction.ReferToCompanyAdmin: {
      if (
        anyRmActionAllowed(map, [
          RmBackendAction.Refer,
          RmBackendAction.ReferToCompanyAdmin,
        ])
      ) {
        return true;
      }

      if (
        anyRmActionPresent(map, [
          RmBackendAction.Refer,
          RmBackendAction.ReferToCompanyAdmin,
        ])
      ) {
        return false;
      }

      return undefined;
    }

    default: {
      if (isRmActionAllowed(map, feAction)) {
        return true;
      }

      if (hasRmAction(map, feAction)) {
        return false;
      }

      return undefined;
    }
  }
}

export function resolveRmActionAllowed(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  action: RmActionName | string,
  legacyAllowed = false
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  if (!usesRmBePermissions(storedCompanyPermissions, companyId)) {
    return legacyAllowed;
  }

  const actionPermissions = companyPermission?.actionPermissions ?? [];
  const map = buildRmActionMap(actionPermissions);
  const resolved = resolveRmFeActionFromBeActionMap(
    map,
    action,
    companyPermission
  );

  if (resolved !== undefined) {
    return resolved;
  }

  // BE doesn't send a Refer/ReferToCompanyAdmin entry in actionPermissions
  // for any RM company yet (unlike every other RM action), so it always
  // resolves to "not present" here — which would otherwise hard-block the
  // button even when the frontend's own approval-state check allows it.
  // Defer to legacyAllowed for this one action until BE adds it.
  if (action === RmAction.ReferToCompanyAdmin) {
    return legacyAllowed;
  }

  return false;
}

export function getAllowedRmActions(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): Set<string> {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  if (!usesRmBePermissions(storedCompanyPermissions, companyId)) {
    return new Set();
  }

  const actionPermissions = companyPermission?.actionPermissions ?? [];
  const map = buildRmActionMap(actionPermissions);
  const allowed = new Set<string>();

  (Object.values(RmAction) as string[]).forEach((feAction) => {
    if (
      resolveRmFeActionFromBeActionMap(map, feAction, companyPermission) ===
      true
    ) {
      allowed.add(feAction);
    }
  });

  return allowed;
}
